import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import noop from 'lodash/noop'
import { postNamedCommand, postNamedQuery } from '../service'
import { useAppContext } from '../redux/slices/appContext'

export const useNLQuery = (options = {}) => {
  const { onSuccess = noop, enabled = true } = options

  const { userId } = useAppContext()
  return useQuery({
    queryKey: ['denali-query-nlquery', userId],
    queryFn: async () => {
      return []
    },
    onSuccess,
    enabled
  })
}

export const usePostNLQuery = () => {
  const queryClient = useQueryClient()
  const { userId } = useAppContext()
  return useMutation({
    mutationFn: async ({ prompt }) => {
      const { data } = await postNamedQuery('denali', 'nlQuery', {
        prompt
      })

      return data
    },
    onSuccess: (data, command) => {
      queryClient.setQueryData(['denali-query-nlquery', userId], prev => {
        if (!prev) {
          return [{ ...command, result: data?.result }]
        }
        prev.push({ ...command, result: data?.result })
        return prev
      })
    }
  })
}

export const useDenaliAssistantThread = (options = {}) => {
  const { onSuccess = noop, enabled = true } = options

  const { userId } = useAppContext()
  return useQuery({
    queryKey: ['denali-assistant-thread', userId],
    queryFn: async () => {
      return []
    },
    onSuccess,
    enabled
  })
}

export const useInvokeAssistant = () => {
  const queryClient = useQueryClient()
  const { userId } = useAppContext()
  return useMutation({
    mutationFn: async ({ prompt }) => {
      const { data } = await postNamedCommand('denali', 'invoke-assistant', {
        prompt
      })

      return data
    },
    onSuccess: (data, command) => {
      queryClient.setQueryData(['denali-assistant-thread', userId], prev => {
        if (!prev) {
          return [{ queryId: data.queryId }]
        }
        prev.push({ queryId: data.queryId })
        return prev
      })
    }
  })
}

export const useAssistantQuery = (queryId, options = {}) => {
  const { userId } = useAppContext()
  const { enabled = true, refetchInterval = null } = options
  return useQuery({
    queryKey: ['denali-assistant-query', userId, queryId],
    queryFn: async () => {
      const { data } = await postNamedQuery('denali', 'get-assistant-query', {
        queryId
      })

      return data
    },
    enabled,
    refetchInterval,
    refetchOnWindowFocus: !!refetchInterval
  })
}

// Query Blueprints - Queries
export const useSearchQueryBlueprints = (query = {}, options = {}) => {
  const { enabled = true } = options
  return useQuery({
    queryKey: ['search-query-blueprints', query],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'search-query-blueprints', query)
      return data
    },
    enabled
  })
}

export const useGetQueryBlueprint = (blueprintId, options = {}) => {
  const { enabled = !!blueprintId, includes = {} } = options
  return useQuery({
    queryKey: ['get-query-blueprint', blueprintId, includes],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'get-query-blueprint', {
        blueprintId,
        includes
      })
      return data
    },
    enabled
  })
}

export const useGetQueryBlueprintDefinition = (definitionId, options = {}) => {
  const { enabled = !!definitionId } = options
  return useQuery({
    queryKey: ['get-query-blueprint-definition', definitionId],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'get-query-blueprint-definition', {
        definitionId
      })
      return data
    },
    enabled
  })
}

export const useListQueryBlueprintVersions = (blueprintId, options = {}) => {
  const { enabled = !!blueprintId } = options
  return useQuery({
    queryKey: ['list-query-blueprint-versions', blueprintId],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'list-query-blueprint-versions', {
        blueprintId
      })
      return data
    },
    enabled
  })
}

// Query Blueprints - Mutations
export const useCreateQueryBlueprint = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('coreData', 'create-query-blueprint', command)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['search-query-blueprints'])
    }
  })
}

export const useModifyQueryBlueprint = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('coreData', 'modify-query-blueprint', command)
      return data
    },
    onSuccess: (data, command) => {
      queryClient.invalidateQueries(['search-query-blueprints'])
      queryClient.invalidateQueries(['get-query-blueprint', command.blueprintId])
    }
  })
}

export const useRemoveQueryBlueprint = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('coreData', 'remove-query-blueprint', command)
      return data
    },
    onSuccess: (data, command) => {
      queryClient.invalidateQueries(['search-query-blueprints'])
      queryClient.removeQueries(['get-query-blueprint', command.blueprintId])
      queryClient.removeQueries(['list-query-blueprint-versions', command.blueprintId])
    }
  })
}

export const useAddQueryBlueprintDefinition = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('coreData', 'add-query-blueprint-definition', command)
      return data
    },
    onSuccess: (data, command) => {
      queryClient.invalidateQueries(['get-query-blueprint', command.blueprintId])
      queryClient.invalidateQueries(['list-query-blueprint-versions', command.blueprintId])
    }
  })
}

export const useModifyQueryBlueprintDefinition = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('coreData', 'modify-query-blueprint-definition', command)
      return data
    },
    onSuccess: (data, command) => {
      queryClient.invalidateQueries(['get-query-blueprint', command.blueprintId])
      queryClient.invalidateQueries(['list-query-blueprint-versions', command.blueprintId])
    }
  })
}

export const useRemoveQueryBlueprintVersion = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('coreData', 'remove-query-blueprint-version', command)
      return data
    },
    onSuccess: (data, command) => {
      queryClient.invalidateQueries(['list-query-blueprint-versions', command.blueprintId])
      queryClient.invalidateQueries(['get-query-blueprint', command.blueprintId])
    }
  })
}

export const useSetActiveQueryBlueprintVersion = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('coreData', 'set-active-query-blueprint-version', command)
      return data
    },
    onSuccess: (data, command) => {
      queryClient.invalidateQueries(['get-query-blueprint', command.blueprintId])
      queryClient.invalidateQueries(['search-query-blueprints'])
    }
  })
}

export const useParseBlueprintFromDql = () => {
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('coreData', 'parse-blueprint-from-dql', command)
      return data
    }
  })
}

export const useAnalyzeDqlQuery = () => {
  return useMutation({
    mutationFn: async (request) => {
      const { data } = await postNamedQuery('coreData', 'analyze-dql-query', request)
      return data
    }
  })
}

export const useExecuteQueryBlueprint = () => {
  return useMutation({
    mutationFn: async (request) => {
      const { data } = await postNamedQuery('coreData', 'execute-query-blueprint', request)
      return data
    }
  })
}

// User DQL Queries - Queries
export const useListUserDqlQueries = (options = {}) => {
  const { enabled = true, favoritesOnly = false } = options
  const { userId } = useAppContext()

  return useQuery({
    queryKey: ['list-user-dql-queries', userId, favoritesOnly],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'list-user-dql-queries', {
        favoritesOnly
      })
      return data
    },
    enabled
  })
}

export const useGetUserDqlQuery = (userDqlQueryId, options = {}) => {
  const { enabled = !!userDqlQueryId } = options

  return useQuery({
    queryKey: ['get-user-dql-query', userDqlQueryId],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'get-user-dql-query', {
        userDqlQueryId
      })
      return data
    },
    enabled
  })
}

// User DQL Queries - Mutations
export const useCreateUserDqlQuery = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('coreData', 'create-user-dql-query', command)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['list-user-dql-queries'] }).catch(console.error)
    }
  })
}

export const useModifyUserDqlQuery = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('coreData', 'modify-user-dql-query', command)
      return data
    },
    onSuccess: (data, command) => {
      queryClient.invalidateQueries({ queryKey: ['list-user-dql-queries'] }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: ['get-user-dql-query', command.userDqlQueryId] }).catch(console.error)
    }
  })
}

export const useRemoveUserDqlQuery = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('coreData', 'remove-user-dql-query', command)
      return data
    },
    onSuccess: (data, command) => {
      queryClient.invalidateQueries({ queryKey: ['list-user-dql-queries'] }).catch(console.error)
      queryClient.removeQueries({ queryKey: ['get-user-dql-query', command.userDqlQueryId] })
    }
  })
}

export const useFavoriteUserDqlQuery = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (command) => {
      const { data } = await postNamedCommand('coreData', 'favorite-user-dql-query', command)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['list-user-dql-queries'] }).catch(console.error)
    }
  })
}
